<script lang="ts" module>
	import type { MediaSrc } from "@components/common/MediaSrc.js";
	import type { ClassValue } from "svelte/elements";
	import youtubeIcon from "@core/assets/icons/youtube.svg?raw";
	import type { Media } from "@core/schema/Media.js";
	import type { SourceSetQuery } from "@core/schema/SourceSetQuery.js";
	import { getThumbnailURLFromYoutubeURL } from "@core/schema/utils/getThumbnailURLFromYoutubeURL.js";
	import { parseSourceSet } from "@core/schema/utils/parseSourceSet.js";
	import { sortSourceSetQuery } from "@core/schema/utils/sortSourceSetQuery.js";
	import { getUploadedFilePath } from "@core/utils/getUploadedFilePath.js";
	import { lazyLoadVideo } from "@core/utils/lazyLoadVideo.js";
	import { thumbUploadedFile } from "@core/utils/thumbUploadedFile.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import Icon from "./Icon.svelte";
	import Lightbox from "./Lightbox.svelte";

	type Sourceset = string | Media | [number, number] | [number, number, Media];
</script>

<script lang="ts">
	// eslint-disable-next-line svelte/no-unused-props
	const all: {
		width: number;
		height: number;
		alt: string;
		eager?: boolean;
		autoplay?: boolean;
		loop?: boolean;
		muted?: boolean;
		playsInline?: boolean;
		noControls?: boolean;
		class?: ClassValue;
		draggable?: boolean;
		wrapperClass?: ClassValue;
		poster?: Media | string;
		sourcesets: Partial<Record<SourceSetQuery, Sourceset>>;
		lightbox?: boolean | string;
		lightboxClass?: ClassValue;
		lightboxWidth?: number;
		source: MediaSrc;
	} = $props();

	const {
		class: customClass,
		wrapperClass,
		eager = false,
		draggable,
		autoplay,
		height,
		width,
		alt,
		loop = false,
		muted = false,
		playsInline = true,
		noControls = false,
		sourcesets,
		lightbox,
		lightboxClass,
		lightboxWidth = 1920,
	} = $derived(all);

	const { FILE_BUCKET_URL_MAP } = getGlobalSettings();

	const sortedSourceset = $derived(
		Object.entries(sourcesets).toSorted(([firstQuery], [secondQuery]) =>
			sortSourceSetQuery(firstQuery as SourceSetQuery, secondQuery as SourceSetQuery),
		) as [SourceSetQuery, [number, number] | [number, number, Media]][],
	);

	/* eslint-disable @typescript-eslint/no-confusing-void-expression, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument */
</script>

{#snippet sourceset(query: SourceSetQuery, set: Sourceset, type: string, parentMedia: Media | null)}
	{#if typeof set === "string"}
		{#if type.startsWith("video")}
			<source srcset="{set}#t=0.001" {type} media={parseSourceSet(query)} />
		{:else}
			<source srcset={set} {type} media={parseSourceSet(query)} />
		{/if}
	{:else if "length" in set}
		{@const [height, width, media] = set}
		{#if media !== undefined && typeof media === "object"}
			{#if media.type.startsWith("image")}
				<source
					srcset={thumbUploadedFile(width, height, media, FILE_BUCKET_URL_MAP)}
					type="image/webp"
					media={parseSourceSet(query)}
				/>
			{:else}
				<source
					srcset={getUploadedFilePath(media, FILE_BUCKET_URL_MAP)}
					type={media.type}
					media={parseSourceSet(query)}
				/>
			{/if}
		{:else if parentMedia?.type.startsWith("image")}
			<source
				srcset={thumbUploadedFile(width, height, parentMedia, FILE_BUCKET_URL_MAP)}
				type="image/webp"
				media={parseSourceSet(query)}
			/>
		{/if}
	{:else}
		<source srcset={getUploadedFilePath(set, FILE_BUCKET_URL_MAP)} type={set.type} media={parseSourceSet(query)} />
	{/if}
{/snippet}

{#snippet video(src: string, type: string, media: Media | null)}
	{@const poster =
		all.poster !== undefined
			? typeof all.poster === "string"
				? all.poster
				: thumbUploadedFile(1920, 0, all.poster, FILE_BUCKET_URL_MAP)
			: undefined}

	<video
		use:lazyLoadVideo={{ enabled: !eager, autoplay }}
		autoplay={autoplay && eager}
		{height}
		{width}
		{loop}
		{muted}
		{draggable}
		playsinline={playsInline}
		controls={!noControls}
		class={[customClass, wrapperClass]}
		{poster}
		preload={eager ? undefined : "none"}
	>
		{#each sortedSourceset as [query, set] (query)}
			{@render sourceset(query, set, type, media)}
		{/each}

		<source src="{src}#t=0.001" {type} />
	</video>
{/snippet}

{#snippet picture(src: string, type: string, media: Media | null)}
	<picture class={wrapperClass}>
		{#each sortedSourceset as [query, set] (query)}
			{@render sourceset(query, set, type, media)}
		{/each}

		<img {draggable} class={customClass} {alt} {src} {width} {height} loading={eager ? "eager" : "lazy"} />
	</picture>
{/snippet}

{#snippet root()}
	{#if "src" in all.source}
		{#if all.source.type.startsWith("video")}
			{@render video(all.source.src, all.source.type, null)}
		{:else if all.source.type}
			{@render picture(all.source.src, all.source.type, null)}
		{/if}
	{:else if "youtube" in all.source}
		{@render picture(getThumbnailURLFromYoutubeURL(all.source.youtube), "", null)}
	{:else if all.source.type.startsWith("video")}
		{@render video(getUploadedFilePath(all.source, FILE_BUCKET_URL_MAP), all.source.type, all.source)}
	{:else}
		{@render picture(getUploadedFilePath(all.source, FILE_BUCKET_URL_MAP), all.source.type, all.source)}
	{/if}
{/snippet}

{#if lightbox}
	{@const id = typeof lightbox === "string" ? lightbox : undefined}
	{@const caption = alt}

	<Lightbox
		class={[lightboxClass, "relative"]}
		source={"youtube" in all.source
			? { href: all.source.youtube.toString() }
			: "src" in all.source
				? { href: all.source.src }
				: { media: all.source, width: lightboxWidth }}
		{id}
		{caption}
		type={"youtube" in all.source ? "youtube" : all.source.type.startsWith("video") ? "video" : "image"}
	>
		{@render root()}
		{#if "youtube" in all.source}
			<div class="absolute bg-white left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-8"></div>
			<Icon
				icon={youtubeIcon}
				class={[
					"w-1/5 text-[#FF0201] absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
					"[a:hover>&]:scale-125 [a:hover>&]:brightness-150 transition-[filter,transform]",
				]}
			/>
		{/if}
	</Lightbox>
{:else}
	{@render root()}
{/if}
